/**
 * React access to the engine (web.md §3): `EngineProvider` owns the QueryClient and turns engine events
 * into cache invalidations; `useEngine`, `useEngineQuery`, `useEngineStatus`, `useEngineEvent` and
 * `useOptimistic` are what screens use. No other state library.
 */

import { QueryClient, QueryClientProvider, useQuery, useQueryClient, type QueryKey, type UseQueryOptions, type UseQueryResult } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useSyncExternalStore, type ReactNode } from 'react';
import type { DigesterError, Engine, EngineEvent, PipelineStatus, SyncStatus } from '@digester/core';
import type { EngineClient, EngineClientStatus } from './client.js';
import { keysForEvent } from './invalidate.js';
import { qk } from './keys.js';
import { optimistic, type Patcher } from './optimistic.js';

interface EngineContextValue {
  client: EngineClient;
  engine: Engine;
}

const EngineContext = createContext<EngineContextValue | null>(null);

export function createEngineQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: Infinity,
        gcTime: 5 * 60_000,
        retry: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
      mutations: { retry: false },
    },
  });
}

/** Apply one engine event to a QueryClient (exported for tests and non-React hosts). */
export function applyEngineEvent(queryClient: QueryClient, event: EngineEvent): void {
  if (event.type === 'sync') queryClient.setQueryData<SyncStatus>(qk.sync(), event.status);
  if (event.type === 'pipeline') queryClient.setQueryData<PipelineStatus>(qk.pipeline(), event.status);
  for (const key of keysForEvent(event)) {
    if (event.type === 'pipeline' && key[0] === 'pipeline') continue;
    void queryClient.invalidateQueries({ queryKey: key as QueryKey });
  }
}

export function EngineProvider({ client, queryClient, children }: { client: EngineClient; queryClient?: QueryClient; children: ReactNode }) {
  const qc = useMemo(() => queryClient ?? createEngineQueryClient(), [queryClient]);
  useEffect(() => client.engine.subscribe((event) => applyEngineEvent(qc, event)), [client, qc]);
  const value = useMemo(() => ({ client, engine: client.engine }), [client]);
  return (
    <EngineContext.Provider value={value}>
      <QueryClientProvider client={qc}>{children}</QueryClientProvider>
    </EngineContext.Provider>
  );
}

function useEngineContext(): EngineContextValue {
  const value = useContext(EngineContext);
  if (!value) throw new Error('useEngine must be used inside <EngineProvider>');
  return value;
}

export function useEngine(): Engine {
  return useEngineContext().engine;
}

export function useEngineClient(): EngineClient {
  return useEngineContext().client;
}

/** Leader, durability and fatal host errors, re-rendering on change. */
export function useEngineStatus(): EngineClientStatus {
  const { client } = useEngineContext();
  return useSyncExternalStore(
    (onChange) => client.onStatus(onChange),
    () => client.status(),
    () => client.status(),
  );
}

/** A cached engine read. Fresh until an engine event invalidates its key (staleTime: Infinity). */
export function useEngineQuery<T>(
  key: QueryKey,
  read: (engine: Engine) => Promise<T>,
  options: Omit<UseQueryOptions<T, DigesterError, T, QueryKey>, 'queryKey' | 'queryFn'> = {},
): UseQueryResult<T, DigesterError> {
  const engine = useEngine();
  return useQuery<T, DigesterError, T, QueryKey>({ ...options, queryKey: key, queryFn: () => read(engine) });
}

/** Listen to raw engine events (toasts, banners). The latest listener is used without resubscribing. */
export function useEngineEvent(listener: (event: EngineEvent) => void): void {
  const engine = useEngine();
  const ref = useRef(listener);
  ref.current = listener;
  useEffect(() => engine.subscribe((event) => ref.current(event)), [engine]);
}

/** `optimistic()` bound to this provider's QueryClient. */
export function useOptimistic(): <T>(change: (p: Patcher) => void, run: () => Promise<T>) => Promise<T> {
  const qc = useQueryClient();
  return useCallback(<T,>(change: (p: Patcher) => void, run: () => Promise<T>) => optimistic(qc, change, run), [qc]);
}
